import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { swapDatabase, SwapTransaction } from '../../services/swapDatabase';
import { transactionService, RecentTransaction } from '../../services/transactionService';
import { getTokenConfig } from '../../config/tokens';
import { getBlockExplorerUrl } from '../../config/networks';
import './Scanner.css';

const Scanner: React.FC = () => {
  const [transactions, setTransactions] = useState<SwapTransaction[]>([]);
  const [selectedTransaction, setSelectedTransaction] = useState<SwapTransaction | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'confirmed' | 'failed'>('all');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Mobile-specific states
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [touchStartY, setTouchStartY] = useState(0);
  const [isVoiceSearchSupported, setIsVoiceSearchSupported] = useState(false);
  const [isVoiceSearchActive, setIsVoiceSearchActive] = useState(false);
  
  // Refs for mobile functionality
  const searchInputRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const pullRefreshRef = useRef<HTMLDivElement>(null);

  const ITEMS_PER_PAGE = 10;

  // Additional mobile states
  const [isQRScannerOpen, setIsQRScannerOpen] = useState(false);
  const [qrScanResult, setQRScanResult] = useState<string>('');
  const [, setSwipeDirection] = useState<'left' | 'right' | null>(null);

  // Check for voice search support and camera access
  useEffect(() => {
    const checkVoiceSupport = () => {
      const speechRecognition = 'webkitSpeechRecognition' in window || 'SpeechRecognition' in window;
      setIsVoiceSearchSupported(speechRecognition);
    };
    checkVoiceSupport();
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback((query: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    debounceTimerRef.current = setTimeout(() => {
      setSearchQuery(query);
      setCurrentPage(1);
    }, 300); // 300ms debounce delay
  }, []);

  // Mobile haptic feedback
  const triggerHapticFeedback = useCallback((type: 'light' | 'medium' | 'heavy' = 'light') => {
    if ('vibrate' in navigator) {
      const patterns = {
        light: [10],
        medium: [20],
        heavy: [30, 10, 30]
      };
      navigator.vibrate(patterns[type]);
    }
  }, []);

  // Voice search functionality
  const startVoiceSearch = useCallback(() => {
    if (!isVoiceSearchSupported) return;

    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    setIsVoiceSearchActive(true);
    triggerHapticFeedback('medium');

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      setSearchQuery(transcript);
      debouncedSearch(transcript);
      triggerHapticFeedback('light');
    };

    recognition.onerror = () => {
      triggerHapticFeedback('heavy');
      setIsVoiceSearchActive(false);
    };

    recognition.onend = () => {
      setIsVoiceSearchActive(false);
    };

    recognition.start();
  }, [isVoiceSearchSupported, debouncedSearch, triggerHapticFeedback]);

  // Generate search suggestions based on existing transactions
  const generateSearchSuggestions = useCallback(async (query: string) => {
    if (query.length < 2) {
      setSearchSuggestions([]);
      return;
    }

    try {
      const allTransactions = await swapDatabase.getSwaps(100, 0);
      const suggestions = new Set<string>();
      
      allTransactions.forEach(tx => {
        // Add token suggestions
        if (tx.fromToken.toLowerCase().includes(query.toLowerCase())) {
          suggestions.add(tx.fromToken);
        }
        if (tx.toToken.toLowerCase().includes(query.toLowerCase())) {
          suggestions.add(tx.toToken);
        }
        
        // Add partial address suggestions
        if (tx.userAddress.toLowerCase().includes(query.toLowerCase())) {
          suggestions.add(`${tx.userAddress.slice(0, 10)}...${tx.userAddress.slice(-8)}`);
        }
        
        // Add transaction hash suggestions
        if (tx.txHash.toLowerCase().includes(query.toLowerCase())) {
          suggestions.add(`${tx.txHash.slice(0, 10)}...${tx.txHash.slice(-8)}`);
        }
      });

      setSearchSuggestions(Array.from(suggestions).slice(0, 5));
    } catch (error) {
      console.error('Failed to generate search suggestions:', error);
    }
  }, []);

  // QR Code scanning functionality
  const startQRScanner = useCallback(() => {
    setIsQRScannerOpen(true);
    triggerHapticFeedback('medium');
    
    // In a real implementation, you would integrate with a QR scanning library
    // For now, we'll simulate QR scanning
    setTimeout(() => {
      // Simulate scanning a transaction hash
      const mockQRResult = '0x1234567890abcdef1234567890abcdef12345678';
      setQRScanResult(mockQRResult);
      setSearchQuery(mockQRResult);
      setIsQRScannerOpen(false);
      triggerHapticFeedback('light');
      handleSearch();
    }, 3000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerHapticFeedback]);

  // Swipe gesture handling for clearing search
  const handleSwipeGesture = useCallback((direction: 'left' | 'right') => {
    setSwipeDirection(direction);
    triggerHapticFeedback('medium');
    
    if (direction === 'right' && searchQuery) {
      // Swipe right to clear search
      clearSearch();
    } else if (direction === 'left') {
      // Swipe left to refresh
      loadTransactions();
    }
    
    setTimeout(() => setSwipeDirection(null), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, triggerHapticFeedback]);

  // Pull-to-refresh functionality
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    setTouchStartY(e.touches[0].clientY);
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!pullRefreshRef.current) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY;
    
    // Only trigger on downward swipe at top of page
    if (diff > 50 && window.scrollY === 0 && !isRefreshing) {
      e.preventDefault();
      pullRefreshRef.current.style.transform = `translateY(${Math.min(diff - 50, 80)}px)`;
    }
  }, [touchStartY, isRefreshing]);

  const handleTouchEnd = useCallback(async (e: React.TouchEvent) => {
    if (!pullRefreshRef.current) return;
    
    const currentY = e.changedTouches[0].clientY;
    const diff = currentY - touchStartY;
    
    pullRefreshRef.current.style.transform = 'translateY(0px)';
    
    if (diff > 100 && window.scrollY === 0 && !isRefreshing) {
      setIsRefreshing(true);
      triggerHapticFeedback('medium');
      
      try {
        await loadTransactions();
        triggerHapticFeedback('light');
      } catch (error) {
        triggerHapticFeedback('heavy');
      } finally {
        setTimeout(() => setIsRefreshing(false), 500);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [touchStartY, isRefreshing, triggerHapticFeedback]);

  // Enhanced search input handler with mobile optimizations
  const handleSearchInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    
    // Generate suggestions for mobile
    if (value.trim()) {
      generateSearchSuggestions(value);
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
      setSearchSuggestions([]);
    }
    
    // Debounced search for performance
    debouncedSearch(value);
  }, [debouncedSearch, generateSearchSuggestions]);

  // Handle search suggestion selection
  const handleSuggestionSelect = useCallback((suggestion: string) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
    setCurrentPage(1);
    triggerHapticFeedback('light');
    
    // Dismiss keyboard on mobile
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  }, [triggerHapticFeedback]);

  // Clear search functionality
  const clearSearch = useCallback(() => {
    setSearchQuery('');
    setShowSuggestions(false);
    setSearchSuggestions([]);
    setCurrentPage(1);
    triggerHapticFeedback('light');
    
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [triggerHapticFeedback]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadTransactions();
    
    // Subscribe to real-time transaction updates
    const unsubscribe = transactionService.subscribe((newTransaction: RecentTransaction) => {
      console.log('📡 Scanner received new transaction:', newTransaction);
      // Refresh transactions when new ones are generated
      loadTransactions();
    });
    
    // Auto refresh every 5 seconds (reduced from 10 for better responsiveness)
    const interval = setInterval(loadTransactions, 5000);
    
    return () => {
      clearInterval(interval);
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, filterStatus, searchQuery]);

  const loadTransactions = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch both swap transactions (USDP20 internal) and token transfers (real blockchain - BSC + Solana)
      console.log('🔄 Loading transactions from all sources...');
      const [swapTxs, allTransfers] = await Promise.all([
        swapDatabase.getSwaps(1000, 0),
        transactionService.getUSDPTransfers(100)
      ]);

      console.log('📊 USDP20 Internal Swaps fetched:', swapTxs.length);
      console.log('📊 Token Transfers (CSV + BSCScan + Solscan) fetched:', allTransfers.length);
      console.log('📊 Sample USDP20 swap:', swapTxs[0]);
      console.log('📊 Sample transfer:', allTransfers[0]);

      // Mark USDP20 internal swaps with a custom chainId to distinguish from real blockchain
      const usdp20Swaps = swapTxs.map(swap => ({
        ...swap,
        chainId: swap.chainId === 56 ? 5600 : swap.chainId, // Mark BSC swaps as USDP20 internal (5600)
        networkName: swap.chainId === 56 ? 'USDP20 Network' : swap.networkName,
        type: (swap.type || 'swap') as 'swap' | 'transfer'
      }));

      // Convert transfers (RecentTransaction[]) to SwapTransaction format
      // Detect source based on address format (Solana = base58, BSC = 0x)
      console.log('🔄 Converting transfers to swap format...');
      console.log('🔄 Raw transfers count:', allTransfers.length);
      if (allTransfers.length > 0) {
        console.log('🔄 First transfer details:', JSON.stringify(allTransfers[0], null, 2));
      }
      
      const transfersAsSwaps: SwapTransaction[] = allTransfers.map((transfer, index) => {
        // Detect if this is a Solana transaction by checking address format
        // Solana addresses are base58 encoded (no 0x prefix), BSC addresses start with 0x
        const isSolana = !transfer.from.startsWith('0x') || !transfer.to.startsWith('0x');
        
        return {
          id: isSolana ? `sol_transfer_${index}_${transfer.txHash}` : `bep20_transfer_${index}_${transfer.txHash}`,
          fromToken: transfer.from, // This is the from address
          toToken: transfer.to, // This is the to address
          fromAmount: transfer.fromAmount,
          toAmount: transfer.toAmount,
          fromTokenLogo: transfer.fromLogo,
          toTokenLogo: transfer.toLogo,
          userAddress: transfer.txHash, // Using txHash as userAddress for display
          txHash: transfer.txHash,
          blockNumber: 0,
          gasUsed: '0',
          gasPrice: '0',
          timestamp: transfer.timestamp,
          status: transfer.status,
          chainId: isSolana ? 9999 : 56, // 9999 = Solana, 56 = BSC
          networkName: isSolana ? 'Solana' : 'BNB Smart Chain',
          priceImpact: 0,
          slippage: 0,
          route: [transfer.from],
          fromTokenAddress: null,
          toTokenAddress: null,
          type: transfer.transactionType === 'transfer' ? 'transfer' as const : 'swap' as const
        };
      });

      console.log('📊 Converted transfers:', transfersAsSwaps.length);
      if (transfersAsSwaps.length > 0) {
        console.log('📊 Sample converted transfer:', JSON.stringify(transfersAsSwaps[0], null, 2));
      }

      // Combine USDP20 internal swaps and real blockchain transfers (BSC + Solana)
      const allTxs = [...usdp20Swaps, ...transfersAsSwaps].sort((a, b) => b.timestamp - a.timestamp);
      console.log('📊 Total combined transactions:', allTxs.length, '(USDP20:', usdp20Swaps.length, ', Transfers:', transfersAsSwaps.length, ')');
      let filteredTxs = allTxs;

      // Apply status filter
      if (filterStatus !== 'all') {
        filteredTxs = allTxs.filter(tx => tx.status === filterStatus);
      }

      // Apply search filter with enhanced mobile search capabilities
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase().trim();
        filteredTxs = filteredTxs.filter(tx =>
          tx.txHash.toLowerCase().includes(query) ||
          tx.fromToken.toLowerCase().includes(query) ||
          tx.toToken.toLowerCase().includes(query) ||
          tx.userAddress.toLowerCase().includes(query) ||
          // Enhanced search: include network name and formatted amounts
          tx.networkName.toLowerCase().includes(query) ||
          `${tx.fromAmount} ${tx.fromToken}`.toLowerCase().includes(query) ||
          `${tx.toAmount} ${tx.toToken}`.toLowerCase().includes(query) ||
          // Search by transaction type
          (tx.type === 'transfer' && 'transfer'.includes(query)) ||
          (tx.type === 'swap' && 'swap'.includes(query))
        );
      }

      // Pagination
      const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
      const endIndex = startIndex + ITEMS_PER_PAGE;
      const paginatedTxs = filteredTxs.slice(startIndex, endIndex);

      setTransactions(paginatedTxs);
      setTotalPages(Math.ceil(filteredTxs.length / ITEMS_PER_PAGE));
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, filterStatus, searchQuery]);

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const dateStr = date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
    const timeStr = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    return `${dateStr}, ${timeStr}`;
  };

  const formatAmount = (amount: string, token: string) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return '0';
    
    if (token === 'WBTC') {
      return num.toFixed(3);
    }
    if (num < 0.01) return num.toFixed(6);
    if (num < 1000) return num.toFixed(2);
    return num.toLocaleString();
  };

  const truncateHash = (hash: string) => {
    return `${hash.slice(0, 8)}...${hash.slice(-6)}`;
  };

  const truncateAddress = (address: string) => {
    if (!address || address.length < 10) return address;
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyAddress = async (address: string, label: string) => {
    try {
      await navigator.clipboard.writeText(address);
      triggerHapticFeedback('light');
      // Could add toast notification here
      console.log(`Copied ${label}: ${address}`);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // Could add toast notification here
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleSearch = useCallback(() => {
    setCurrentPage(1);
    setShowSuggestions(false);
    loadTransactions();
    triggerHapticFeedback('light');
    
    // Dismiss keyboard on mobile
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  }, [loadTransactions, triggerHapticFeedback]);

  // Enhanced keyboard event handler for mobile
  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
      setShowSuggestions(false);
      
      // Dismiss keyboard on mobile
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
      
      triggerHapticFeedback('light');
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      if (searchInputRef.current) {
        searchInputRef.current.blur();
      }
    }
  }, [handleSearch, triggerHapticFeedback]);

  return (
    <div
      className="scanner-page"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <div className="scanner-container">
        {/* Pull-to-refresh indicator */}
        <div ref={pullRefreshRef} className={`pull-refresh-indicator ${isRefreshing ? 'refreshing' : ''}`}>
          <div className="refresh-spinner"></div>
          <span>Pull to refresh</span>
        </div>

        {/* Hero Section */}
        <motion.div
          className="scanner-hero"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="scanner-title">
            USDP20 <span className="scanner-title-highlight">Scanner</span>
          </h1>
          <p className="scanner-subtitle">
            Track and explore swap transactions on the USDP20 blockchain network.
          </p>
        </motion.div>

        {/* Enhanced Mobile Search Section */}
        <motion.div
          className="scanner-search"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="search-container">
            <div className="mobile-search-wrapper">
              <div className="search-input-container">
                <input
                  ref={searchInputRef}
                  type="search"
                  className={`search-input-field mobile-optimized ${isSearchFocused ? 'focused' : ''}`}
                  placeholder="Search transactions, tokens, addresses..."
                  value={searchQuery}
                  onChange={handleSearchInputChange}
                  onKeyPress={handleKeyPress}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                  // Mobile-specific attributes
                  inputMode="search"
                  autoComplete="off"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  enterKeyHint="search"
                />
                
                {/* Search action buttons */}
                <div className="search-actions">
                  {/* Mobile search loading indicator */}
                  {loading && (
                    <div className="mobile-search-loading">
                      <div className="mobile-search-spinner"></div>
                    </div>
                  )}
                  
                  {searchQuery && (
                    <button
                      className="clear-search-btn mobile-btn"
                      onClick={clearSearch}
                      aria-label="Clear search"
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  )}
                  
                  {/* QR Code Scanner Button */}
                  <button
                    className="qr-scanner-btn mobile-btn"
                    onClick={startQRScanner}
                    aria-label="Scan QR code"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="3" width="5" height="5"></rect>
                      <rect x="16" y="3" width="5" height="5"></rect>
                      <rect x="3" y="16" width="5" height="5"></rect>
                      <path d="m21 16-3.5-3.5-2.5 2.5"></path>
                      <path d="M13 13l3 3 3-3"></path>
                      <path d="M9 9v0"></path>
                    </svg>
                  </button>
                  
                  {isVoiceSearchSupported && (
                    <button
                      className={`voice-search-btn mobile-btn ${isVoiceSearchActive ? 'active' : ''}`}
                      onClick={startVoiceSearch}
                      disabled={isVoiceSearchActive}
                      aria-label="Voice search"
                    >
                      {isVoiceSearchActive && <div className="voice-search-wave"></div>}
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
                        <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
                        <line x1="12" y1="19" x2="12" y2="23"></line>
                        <line x1="8" y1="23" x2="16" y2="23"></line>
                      </svg>
                    </button>
                  )}
                  
                  <button
                    className="search-btn mobile-btn primary"
                    onClick={handleSearch}
                    aria-label="Search"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"></circle>
                      <path d="m21 21-4.35-4.35"></path>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Search suggestions dropdown for mobile */}
              <AnimatePresence>
                {showSuggestions && searchSuggestions.length > 0 && (
                  <motion.div
                    className="search-suggestions mobile-suggestions"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                  >
                    {searchSuggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        className="suggestion-item"
                        onClick={() => handleSuggestionSelect(suggestion)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="11" cy="11" r="8"></circle>
                          <path d="m21 21-4.35-4.35"></path>
                        </svg>
                        <span>{suggestion}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile-optimized filter controls */}
            <div className="search-controls mobile-controls">
              <select
                className="status-dropdown mobile-dropdown"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as any)}
              >
                <option value="all">All Transactions</option>
                <option value="confirmed">Confirmed</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
          </div>
        </motion.div>

        {/* Transactions Table */}
        <motion.div
          className="transactions-container"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading transactions...</p>
            </div>
          ) : (
            <>
              <div className="transactions-table">
                <div className="table-header">
                  <div className="header-cell">TYPE</div>
                  <div className="header-cell">FROM</div>
                  <div className="header-cell">TO</div>
                  <div className="header-cell">STATUS</div>
                  <div className="header-cell">AMOUNT</div>
                </div>
                
                <div className="table-body">
                  <AnimatePresence>
                    {transactions.map((tx, index) => (
                      <motion.div
                        key={tx.id}
                        className="transaction-row"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.03 }}
                        onClick={() => setSelectedTransaction(tx)}
                      >
                        {/* TYPE Column: Hash + Operation Badge */}
                        <div className="row-cell type-cell" data-label="Type">
                          <div className="type-column-content">
                            <div className="hash-container">
                              {
                                (() => {
                                  // Show explorer link for real blockchain transactions (BSC = chainId 56, Solana = chainId 9999)
                                  // USDP20 internal transactions have chainId 5600 and no explorer
                                  const isBscBlockchain = tx.chainId === 56 && tx.type === 'transfer';
                                  const isSolanaBlockchain = tx.chainId === 9999 && tx.type === 'transfer';
                                  const isRealBlockchain = isBscBlockchain || isSolanaBlockchain;
                                  const explorer = isRealBlockchain ? getBlockExplorerUrl(tx.chainId) : null;
                                  const shortHash = truncateHash(tx.txHash);
                                  return explorer ? (
                                    <a
                                      href={`${explorer}/tx/${tx.txHash}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="hash-text hash-link"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {shortHash}
                                    </a>
                                  ) : (
                                    <span className="hash-text">{shortHash}</span>
                                  );
                                })()
                              }
                              <button
                                className="copy-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyToClipboard(tx.txHash);
                                }}
                                title="Copy transaction hash"
                              >
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                </svg>
                              </button>
                            </div>
                            <span className={`type-badge type-${tx.type || 'swap'}`}>
                              {tx.type === 'transfer' ? '📤 Transfer' : '🔄 Swap'}
                            </span>
                          </div>
                        </div>
                        
                        {/* FROM Column */}
                        <div className="row-cell from-cell" data-label="From">
                          <div className="token-info">
                            {tx.type === 'transfer' ? (
                              <>
                                <div className="address-icon-wrapper">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                  </svg>
                                </div>
                                <div className="address-container">
                                  <span className="address-label">From</span>
                                  <span className="token-symbol address-text" title={tx.fromToken}>
                                    {truncateAddress(tx.fromToken)}
                                  </span>
                                </div>
                                <button
                                  className="copy-address-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyAddress(tx.fromToken, 'From Address');
                                  }}
                                  title="Copy from address"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                  </svg>
                                </button>
                              </>
                            ) : (
                              <>
                                <img src={getTokenConfig(tx.fromToken)?.logoUrl} alt={tx.fromToken} className="token-logo" />
                                <span className="token-symbol">{tx.fromToken}</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* TO Column */}
                        <div className="row-cell to-cell" data-label="To">
                          <div className="token-info">
                            {tx.type === 'transfer' ? (
                              <>
                                <div className="address-icon-wrapper">
                                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                  </svg>
                                </div>
                                <div className="address-container">
                                  <span className="address-label">To</span>
                                  <span className="token-symbol address-text" title={tx.toToken}>
                                    {truncateAddress(tx.toToken)}
                                  </span>
                                </div>
                                <button
                                  className="copy-address-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    copyAddress(tx.toToken, 'To Address');
                                  }}
                                  title="Copy to address"
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                                  </svg>
                                </button>
                              </>
                            ) : (
                              <>
                                <img src={getTokenConfig(tx.toToken)?.logoUrl} alt={tx.toToken} className="token-logo" />
                                <span className="token-symbol">{tx.toToken}</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* STATUS Column */}
                        <div className="row-cell status-cell" data-label="Status">
                          <span className={`status-badge status-${tx.status}`}>
                            {tx.status === 'confirmed' && '✓ '}
                            {tx.status === 'pending' && '⏳ '}
                            {tx.status === 'failed' && '✗ '}
                            {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                          </span>
                        </div>
                        
                        {/* AMOUNT Column: Amounts + Timestamp */}
                        <div className="row-cell amount-cell" data-label="Amount">
                          <div className="amount-column-content">
                            <div className="amount-info">
                              {tx.type === 'transfer' ? (
                                <div className="amount-primary">
                                  {formatAmount(tx.fromAmount, 'USDP')} USDP
                                </div>
                              ) : (
                                <>
                                  <div className="amount-primary">
                                    {formatAmount(tx.fromAmount, tx.fromToken)} {tx.fromToken}
                                  </div>
                                  <div className="amount-secondary">
                                    → {formatAmount(tx.toAmount, tx.toToken)} {tx.toToken}
                                  </div>
                                </>
                              )}
                            </div>
                            <div className="timestamp-info">
                              <span className="time-text">
                                {formatTimestamp(tx.timestamp)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container">
                  <button
                    className="page-button"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  >
                    Previous
                  </button>
                  <div className="page-info">
                    <span>Page {currentPage} of {totalPages}</span>
                  </div>
                  <button
                    className="page-button"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  >
                    Next
                  </button>
                </div>
              )}

              {transactions.length === 0 && !loading && (
                <div className="empty-state">
                  <p>No transactions found.</p>
                </div>
              )}
            </>
          )}
        </motion.div>
      </div>

      {/* Transaction Detail Modal */}
      <AnimatePresence>
        {selectedTransaction && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedTransaction(null)}
          >
            <motion.div
              className="modal-content"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="modal-title-section">
                  {selectedTransaction.type === 'transfer' && (
                    <img src="/USDPlogo.png" alt="USDP" className="modal-token-logo" />
                  )}
                  <div>
                    <h3>{selectedTransaction.type === 'transfer'
                      ? (selectedTransaction.chainId === 9999 ? 'SPL Transfer Details' : 'BEP20 Transfer Details')
                      : 'Transaction Details'
                    }</h3>
                    {selectedTransaction.type === 'transfer' && (
                      <span className="modal-network-badge">
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                          <circle cx="12" cy="12" r="10"/>
                        </svg>
                        {selectedTransaction.chainId === 9999 ? 'Solana' : 'BNB Smart Chain'}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  className="modal-close"
                  onClick={() => setSelectedTransaction(null)}
                >
                  ✕
                </button>
              </div>
              
              <div className="modal-body">
                {/* Enhanced BEP20 Transfer Header */}
                {selectedTransaction.type === 'transfer' && (
                  <div className="transfer-header-card">
                    <div className="transfer-amount-display">
                      <img src="/USDPlogo.png" alt="USDP" className="transfer-token-icon" />
                      <div className="transfer-amount-text">
                        <span className="transfer-amount-value">{formatAmount(selectedTransaction.fromAmount, selectedTransaction.fromToken)}</span>
                        <span className="transfer-amount-token">USDP</span>
                      </div>
                    </div>
                    <div className="transfer-badge-container">
                      <span className="type-badge type-transfer">📤 Transfer</span>
                    </div>
                  </div>
                )}

                {/* Enhanced Explorer Link for BEP20 and Solana Transfers */}
                {(selectedTransaction.chainId === 56 || selectedTransaction.chainId === 9999) && selectedTransaction.type === 'transfer' && (
                  <div className="explorer-link-section">
                    <a
                      href={selectedTransaction.chainId === 9999
                        ? `https://solscan.io/tx/${selectedTransaction.txHash}`
                        : `https://bscscan.com/tx/${selectedTransaction.txHash}`
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      className="explorer-button"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                        <polyline points="15 3 21 3 21 9"></polyline>
                        <line x1="10" y1="14" x2="21" y2="3"></line>
                      </svg>
                      <span>
                        {selectedTransaction.chainId === 9999
                          ? 'View on Solscan Explorer'
                          : 'View on BSCScan Explorer'
                        }
                      </span>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                    </a>
                  </div>
                )}

                <div className="detail-section">
                  <label>Transaction Hash</label>
                  <div className="detail-value hash-value">
                    <code>{selectedTransaction.txHash}</code>
                    <button onClick={() => copyToClipboard(selectedTransaction.txHash)} title="Copy transaction hash">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                        <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                      </svg>
                    </button>
                  </div>
                </div>

                <div className="detail-section">
                  <label>Status</label>
                  <div className="detail-value">
                    <span className={`status-badge status-${selectedTransaction.status}`}>
                      {selectedTransaction.status === 'confirmed' && '✓'}
                      {selectedTransaction.status === 'pending' && '⏳'}
                      {selectedTransaction.status === 'failed' && '✗'}
                      {selectedTransaction.status.charAt(0).toUpperCase() + selectedTransaction.status.slice(1)}
                    </span>
                  </div>
                </div>

                <div className="detail-grid">
                  <div className="detail-section">
                    <label>{selectedTransaction.type === 'transfer' ? 'From Address' : 'From'}</label>
                    <div className="detail-value">
                      {selectedTransaction.type === 'transfer' ? (
                        <div className="address-detail-container">
                          <div className="address-icon-large">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                          </div>
                          <div className="address-text-container">
                            <code className="address-full" title={selectedTransaction.fromToken}>
                              {selectedTransaction.fromToken}
                            </code>
                          </div>
                          <button
                            className="copy-address-btn-large"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyAddress(selectedTransaction.fromToken, 'From Address');
                            }}
                            title="Copy from address"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="token-value">
                          <img src={getTokenConfig(selectedTransaction.fromToken)?.logoUrl} alt={selectedTransaction.fromToken} />
                          <span>{formatAmount(selectedTransaction.fromAmount, selectedTransaction.fromToken)} {selectedTransaction.fromToken}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="detail-section">
                    <label>{selectedTransaction.type === 'transfer' ? 'To Address' : 'To'}</label>
                    <div className="detail-value">
                      {selectedTransaction.type === 'transfer' ? (
                        <div className="address-detail-container">
                          <div className="address-icon-large">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                              <circle cx="12" cy="7" r="4"></circle>
                            </svg>
                          </div>
                          <div className="address-text-container">
                            <code className="address-full" title={selectedTransaction.toToken}>
                              {selectedTransaction.toToken}
                            </code>
                          </div>
                          <button
                            className="copy-address-btn-large"
                            onClick={(e) => {
                              e.stopPropagation();
                              copyAddress(selectedTransaction.toToken, 'To Address');
                            }}
                            title="Copy to address"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                            </svg>
                          </button>
                        </div>
                      ) : (
                        <div className="token-value">
                          <img src={getTokenConfig(selectedTransaction.toToken)?.logoUrl} alt={selectedTransaction.toToken} />
                          <span>{formatAmount(selectedTransaction.toAmount, selectedTransaction.toToken)} {selectedTransaction.toToken}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {selectedTransaction.type === 'transfer' && (
                  <div className="detail-section">
                    <label>Transfer Amount</label>
                    <div className="detail-value">
                      <div className="token-value">
                        <img src="/USDPlogo.png" alt="USDP" style={{width: '24px', height: '24px'}} />
                        <span className="amount-highlight">{formatAmount(selectedTransaction.fromAmount, 'USDP')} USDP</span>
                      </div>
                    </div>
                  </div>
                )}

                <div className="detail-section">
                  <label>Block Number</label>
                  <div className="detail-value">
                    <code>#{selectedTransaction.blockNumber.toLocaleString()}</code>
                  </div>
                </div>

                <div className="detail-grid">
                  <div className="detail-section">
                    <label>Gas Used</label>
                    <div className="detail-value">
                      {parseInt(selectedTransaction.gasUsed).toLocaleString()}
                    </div>
                  </div>
                  
                  <div className="detail-section">
                    <label>Gas Price</label>
                    <div className="detail-value">
                      {parseFloat(selectedTransaction.gasPrice).toFixed(4)} Gwei
                    </div>
                  </div>
                </div>

                <div className="detail-grid">
                  <div className="detail-section">
                    <label>Price Impact</label>
                    <div className="detail-value">
                      {selectedTransaction.priceImpact.toFixed(2)}%
                    </div>
                  </div>
                  
                  <div className="detail-section">
                    <label>Slippage</label>
                    <div className="detail-value">
                      {selectedTransaction.slippage}%
                    </div>
                  </div>
                </div>

                <div className="detail-section">
                  <label>Timestamp</label>
                  <div className="detail-value">
                    {formatTimestamp(selectedTransaction.timestamp)}
                  </div>
                </div>

                <div className="detail-section">
                  <label>Network</label>
                  <div className="detail-value">
                    <span className="network-badge">
                      {selectedTransaction.type === 'transfer'
                        ? (selectedTransaction.chainId === 9999
                            ? 'Solana (SPL)'
                            : 'BNB Smart Chain (BEP20)')
                        : 'USDP20 Blockchain'
                      }
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* QR Code Scanner Modal */}
      <AnimatePresence>
        {isQRScannerOpen && (
          <motion.div
            className="qr-scanner-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsQRScannerOpen(false)}
          >
            <motion.div
              className="qr-scanner-container"
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="qr-scanner-header">
                <h3 className="qr-scanner-title">QR Code Scanner</h3>
                <button
                  className="qr-close-btn"
                  onClick={() => setIsQRScannerOpen(false)}
                  aria-label="Close QR scanner"
                >
                  ✕
                </button>
              </div>
              
              <div className="qr-camera-view">
                <div className="qr-camera-placeholder">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="3" y="3" width="5" height="5"></rect>
                    <rect x="16" y="3" width="5" height="5"></rect>
                    <rect x="3" y="16" width="5" height="5"></rect>
                    <path d="m21 16-3.5-3.5-2.5 2.5"></path>
                    <path d="M13 13l3 3 3-3"></path>
                  </svg>
                  <span>Position QR code within frame</span>
                </div>
              </div>
              
              <p className="qr-scan-instructions">
                Scan a QR code containing a transaction hash, wallet address, or token contract address to search instantly.
              </p>
              
              {qrScanResult && (
                <div className="qr-scan-result">
                  Scanned: {qrScanResult}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Quick Actions */}
      <div className="mobile-quick-actions">
        <button
          className="quick-action-btn"
          onClick={() => handleSwipeGesture('left')}
          aria-label="Refresh transactions"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
            <path d="M21 3v5h-5"></path>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
            <path d="M3 21v-5h5"></path>
          </svg>
          <span>Refresh</span>
        </button>
        
        <button
          className="quick-action-btn"
          onClick={startQRScanner}
          aria-label="Scan QR code"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="5" height="5"></rect>
            <rect x="16" y="3" width="5" height="5"></rect>
            <rect x="3" y="16" width="5" height="5"></rect>
          </svg>
          <span>QR Scan</span>
        </button>
        
        <button
          className="quick-action-btn"
          onClick={() => handleSwipeGesture('right')}
          aria-label="Clear search"
          disabled={!searchQuery}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M3 6h18"></path>
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
          </svg>
          <span>Clear</span>
        </button>
        
        {isVoiceSearchSupported && (
          <button
            className="quick-action-btn"
            onClick={startVoiceSearch}
            disabled={isVoiceSearchActive}
            aria-label="Voice search"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
            </svg>
            <span>Voice</span>
          </button>
        )}
      </div>

    </div>
  );
};

export default Scanner;